// A local, per-user record of settled spins and the client_seed used per epoch. The verify
// flow needs to know WHICH (epoch, nonce, mode, expected-outcome) tuples to recompute when a
// seed is revealed; hub-core doesn't hand back a spin history, so the client keeps its own.
//
// hub-core now reveals client_seed on rotation, so this is not the primary seed source — but
// it also lets us verify epoch 0 (client_seed = the public default formula) and survives a
// reveal that (defensively) lacks the field.

import type { Mode } from './types';

const STORAGE_VERSION = 'v1';

export interface LoggedSpin {
  epoch: number;
  nonce: number;
  mode: Mode;
  prizeTier: string;
  prizeFs: number;
  commitHash: string;
  weightTableHash: string;
  pityAtSettle: number;
  clientSeedUsed: string;
  spinId: string;
  settledAt: number;
}

interface Store {
  spins: LoggedSpin[];
  clientSeeds: Record<string, string>; // epoch -> client_seed
}

export class SpinLog {
  private key: string;
  private store: Store;

  constructor(uid: number) {
    this.key = `sanctum-arcade:${STORAGE_VERSION}:${uid}`;
    this.store = this.load();
  }

  private load(): Store {
    try {
      const raw = localStorage.getItem(this.key);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<Store>;
        return { spins: parsed.spins ?? [], clientSeeds: parsed.clientSeeds ?? {} };
      }
    } catch {
      // Corrupt / unavailable storage — start fresh rather than crash the wheel.
    }
    return { spins: [], clientSeeds: {} };
  }

  private persist(): void {
    try {
      localStorage.setItem(this.key, JSON.stringify(this.store));
    } catch {
      // Storage full / disabled — verify degrades to what the server reveals, no crash.
    }
  }

  logSpin(entry: LoggedSpin): void {
    // De-dupe on spin_id so an idempotent replay doesn't double-record.
    if (this.store.spins.some((s) => s.spinId === entry.spinId)) return;
    this.store.spins.push(entry);
    this.rememberClientSeed(entry.epoch, entry.clientSeedUsed);
    this.persist();
  }

  rememberClientSeed(epoch: number, clientSeed: string): void {
    if (!clientSeed) return;
    if (this.store.clientSeeds[String(epoch)] === clientSeed) return;
    this.store.clientSeeds[String(epoch)] = clientSeed;
    this.persist();
  }

  clientSeedForEpoch(epoch: number): string | undefined {
    return this.store.clientSeeds[String(epoch)];
  }

  spinsForEpoch(epoch: number): LoggedSpin[] {
    return this.store.spins.filter((s) => s.epoch === epoch).sort((a, b) => a.nonce - b.nonce);
  }

  allSpins(): LoggedSpin[] {
    return [...this.store.spins].sort((a, b) =>
      a.epoch === b.epoch ? a.nonce - b.nonce : a.epoch - b.epoch,
    );
  }
}

// The public epoch-0 client_seed formula (hub-core fairness.default_client_seed). Lets the
// browser verify the very first epoch even from a fresh device with no local memory.
export function defaultClientSeed(uid: number, gameId: string): string {
  return `hubcore:${gameId}:${uid}`;
}
